import React, { useState } from 'react';
import {
    Server, Database, Cpu, Users, Building2, Globe2,
    Landmark, Briefcase, Heart, Network, ChevronRight, Target,
    Sparkles, TrendingUp, Shield, BookOpen, Zap,
    Layers, Wheat, Cloud, Lock, Gauge, Smartphone
} from 'lucide-react';
import AICEResponse from "./AICIEResponse";

// 1. Introduction Section - Place after Hero
export function IntroductionSection() {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">


                    <h2 className="text-4xl font-bold text-gray-900 leading-tight">
                        Introduction
                    </h2>

                    <p className="text-lg text-gray-700 leading-relaxed">
                        The Artificial Intelligence Centre of Excellence Africa (AICEX-Africa) is a regional initiative established to strengthen Africa’s capacity to design, deploy, govern, and benefit from Artificial Intelligence (AI) in a sustainable and inclusive manner. The Centre operates within the framework of the African Open Science Platform (AOSP), a continental program hosted by the National Research Foundation (NRF) of South Africa, which aims to position African scientists at the forefront of data-intensive science and AI research.
                    </p>

                    <p className="text-gray-600 text-lg leading-relaxed">
                        As part of the AOSP architecture, the AICAD-hosted Eastern Africa Node (AOSP-EA)—co-hosted with Jomo Kenyatta University of Agriculture and Technology (JKUAT)—was tasked with establishing a regional AI Centre of Excellence. This mandate led to the creation of AICEX-Africa in May 2025, supported by advanced federated cloud infrastructure combining AOSP-EA and GOSC servers, offering 15 PB of storage and 1,392 CPU cores dedicated to research, training, and innovation.
                    </p>


                </div>

                <div className="space-y-4">
                    <div
                        className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                        <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-blue-600"/>
                            Who We Serve
                        </h4>
                        <p className="text-gray-600 text-lg leading-relaxed">
                            AICEX-Africa serves a broad community including universities, research institutes, governments, public agencies, private sector companies, industry partners, civil society organizations, NGOs, and development partners. The Centre is committed to accelerating Africa’s participation in the global AI ecosystem while ensuring that AI advances are equitable, ethical, and fully aligned with local development priorities.
                        </p>
                    </div>


                </div>
            </div>
        </div>
    );
}

// 2. Enhanced Infrastructure Stats - Update in Hero
export function InfrastructureStats() {
    const stats = [
        { value: '15', unit: 'PB', label: 'Storage Capacity', icon: Database },
        { value: '1,392', unit: '', label: 'CPU Cores', icon: Cpu },
        { value: '2', unit: '', label: 'Federated Servers', icon: Server },
        { value: '2025', unit: '', label: 'Established', icon: Sparkles }
    ];

    return (
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">
                    <stat.icon className="w-8 h-8 text-white mb-3 opacity-80" />
                    <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-4xl font-bold text-white">{stat.value}</span>
                        {stat.unit && <span className="text-xl text-white/80 font-semibold">{stat.unit}</span>}
                    </div>
                    <p className="text-sm text-white/90 font-medium">{stat.label}</p>
                </div>
            ))}
        </div>
    );
}

// 3. Strategic Challenges - After Mission & Vision
export function StrategicChallenges() {
    const challenges = [
        {
            icon: Cpu,
            title: 'Limited Computing Access',
            desc: 'Insufficient high-performance computing and data storage infrastructure'
        },
        {
            icon: Database,
            title: 'Data Quality Barriers',
            desc: 'Challenges in collecting and maintaining high-quality, FAIR-compliant datasets'
        },
        {
            icon: Users,
            title: 'Skills Shortage',
            desc: 'Critical gaps in skilled AI professionals and research talent across the continent'
        },
        {
            icon: Globe2,
            title: 'System Misalignment',
            desc: 'Over-dependence on imported AI systems not designed for African realities'
        },
        {
            icon: Shield,
            title: 'Fragmented Governance',
            desc: 'Inconsistent policy, ethics, and data governance approaches across regions'
        },
        {
            icon: TrendingUp,
            title: 'Innovation Barriers',
            desc: 'Limited pathways for communities and SMEs to leverage AI for transformation'
        }
    ];

    return (
        <div className="bg-gray-50 py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">
                        Strategic Context
                    </h2>
                </div>

                <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                    Artificial Intelligence has emerged as a general-purpose technology with transformative potential
                    across sectors such as agriculture, health, education, public administration, financial services,
                    climate action, and national security. Yet many African countries face critical resource, skills,
                    and infrastructure gaps that limit their ability to harness AI for sustainable development.
                    Addressing these disparities requires coordinated efforts to build human capital, invest in shared
                    infrastructure, strengthen policy frameworks, and create enabling ecosystems for responsible
                    innovation.

                </p>

                <p className="text-gray-700 text-lg mb-4 leading-relaxed">
                    The development of the Artificial Intelligence Centre of Excellence Africa (AICEX-Africa) aligns
                    closely with the UN Secretary-General’s report on Innovative Voluntary Financing for AI
                    Capacity-Building, which encourages Member States to establish AI Centres that contribute to a
                    United Nations–supported global ecosystem of responsible AI development. Kenya and other governments
                    have expressed commitment to hosting and supporting such centres. As affirmed by Ambassador Phil
                    Thigo, Kenya’s Special Envoy for Technology: “Capacity-building is crucial for the safe and
                    responsible adoption of AI. We need collaborations to strengthen institutional readiness and close
                    the AI divide in the Global South.” AICEX-Africa’s integration into this global network represents a
                    strategic step toward deepening international cooperation, strengthening responsible innovation, and
                    expanding equitable opportunities in AI.
                </p>

                <p className="text-gray-700 text-lg mb-4 leading-relaxed">
                    AICEX-Africa is designed to help African governments, institutions, and innovators overcome
                    persistent obstacles such as limited access to high-performance computing and data storage; barriers
                    to collecting and maintaining high-quality datasets; shortages of skilled AI professionals and
                    research talent; dependence on imported AI systems misaligned with African realities and fragmented
                    policy, ethics, and data governance approaches. By democratizing access to AI infrastructure and
                    knowledge, the Centre aims to create inclusive pathways for communities, SMEs, start-ups, and public
                    institutions to leverage AI for socioeconomic transformation.
                </p>

                <p className="text-gray-700 text-lg leading-relaxed">
                    AICEX-Africa is committed to ensuring that AI benefits all Africans, regardless of geography, socioeconomic status, or institutional capacity. Meeting this goal requires strong partnerships across governments, multilateral development banks, philanthropies, universities, corporations, and civil society. Through strategic convenings, open-source model libraries, shared infrastructure, and coordinated policy efforts, AICEX-Africa seeks to create a continental ecosystem where AI is developed responsibly, governed ethically, and applied meaningfully to drive Africa’s development agenda.
                </p>

                <AICEResponse/>
            </div>
        </div>
    );
}

// 4. Thematic Priorities - After Objectives
export function ThematicPriorities() {
    const priorities = [
        {
            icon: Layers,
            title: 'Scaling AI Applications',
            desc: 'From pilot projects to sector-wide transformation across industries',
            color: 'blue'
        },
        {
            icon: BookOpen,
            title: 'Digital Storytelling',
            desc: 'Cultural preservation for environmental education and social justice',
            color: 'purple'
        },
        {
            icon: Lock,
            title: 'Cybersecurity Analytics',
            desc: 'Threat detection and security solutions tailored to regional risks',
            color: 'red'
        },
        {
            icon: Smartphone,
            title: 'Embedded AI Systems',
            desc: 'Optimized for constrained environments, affordability, and sustainability',
            color: 'green'
        },
        {
            icon: Gauge,
            title: 'Condition Monitoring',
            desc: 'Optimization for utilities, infrastructure, and critical services',
            color: 'orange'
        },
        {
            icon: Wheat,
            title: 'Agricultural AI',
            desc: 'Climate resilience, food sovereignty, and rural empowerment technologies',
            color: 'emerald'
        }
    ];

    const colorClasses = {
        blue: 'from-blue-50 to-blue-100 border-blue-200 group-hover:border-blue-400',
        purple: 'from-purple-50 to-purple-100 border-purple-200 group-hover:border-purple-400',
        red: 'from-red-50 to-red-100 border-red-200 group-hover:border-red-400',
        green: 'from-green-50 to-green-100 border-green-200 group-hover:border-green-400',
        orange: 'from-orange-50 to-orange-100 border-orange-200 group-hover:border-orange-400',
        emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 group-hover:border-emerald-400'
    };

    const iconColorClasses = {
        blue: 'text-blue-600',
        purple: 'text-purple-600',
        red: 'text-red-600',
        green: 'text-green-600',
        orange: 'text-orange-600',
        emerald: 'text-emerald-600'
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center mb-12">

                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                    Thematic Priorities
                </h2>
                <p className="text-lg text-gray-600 text-lg max-w-3xl mx-auto">
                    Developing locally relevant, sustainable, and resource-aware AI systems
                    for Africa's unique challenges
                </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {priorities.map((priority, idx) => (
                    <div
                        key={idx}
                        className={`bg-white border rounded-2xl p-6 hover:shadow-xl transition-all duration-300 group`}
                    >
                        <priority.icon className={`w-10 h-10 ${iconColorClasses[priority.color]} mb-4`} />
                        <h4 className="text-xl font-bold text-gray-900 mb-3">{priority.title}</h4>
                        <p className="text-gray-700 text-lg leading-relaxed">{priority.desc}</p>
                    </div>
                ))}
            </div>

            <div className="mt-12 bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <div className="flex items-start gap-4">

                    <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">Our Approach</h4>
                        <p className="text-gray-700 text-lg leading-relaxed">
                            All priorities emphasize <span className="font-semibold">open-source solutions</span>,
                            <span className="font-semibold"> multilingual models</span> reflecting African languages and cultures,
                            and <span className="font-semibold">FAIR data principles</span> to ensure accessibility,
                            reproducibility, and long-term sustainability.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// 5. Global Network Highlight - Before Partnerships
export function GlobalNetworkHighlight() {
    return (
        <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 py-20 relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="space-y-6">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                            <Network className="w-4 h-4 text-white" />
                            <span className="text-sm font-semibold text-white">November 2025</span>
                        </div>

                        <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                            Joining a Global Network of AI Excellence
                        </h2>

                        <p className="text-xl text-blue-100 leading-relaxed">
                            AICEX-Africa was invited to join a global network of AI Centres of Excellence,
                            connecting leading institutions across thematic and geographical domains.
                        </p>

                        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                            <p className="text-white leading-relaxed mb-4">
                                This network supports shared best practices, joint research, resource mutualization,
                                and coordinated capacity-building for AI development worldwide.
                            </p>
                            <div className="flex items-center gap-3 text-sm text-blue-200">
                                <Globe2 className="w-5 h-5" />
                                <span>Aligned with UN Secretary-General's AI Capacity-Building Initiative</span>
                            </div>
                        </div>

                        <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                            <div className="flex items-start gap-3">
                                <div className="w-1 h-full bg-blue-400 rounded-full"></div>
                                <div>
                                    <p className="text-white/90 italic leading-relaxed mb-2">
                                        "Capacity-building is crucial for the safe and responsible adoption of AI.
                                        We need collaborations to strengthen institutional readiness and close the
                                        AI divide in the Global South."
                                    </p>
                                    <p className="text-sm text-blue-200 font-medium">
                                        — Ambassador Phil Thigo, Kenya's Special Envoy for Technology
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {[
                            { title: 'Shared Best Practices', desc: 'Learning from global leaders in AI development', icon: BookOpen },
                            { title: 'Joint Research', desc: 'Collaborative projects across continents', icon: Users },
                            { title: 'Resource Mutualization', desc: 'Access to shared infrastructure and tools', icon: Database },
                            { title: 'Coordinated Capacity-Building', desc: 'Unified approach to skills development', icon: TrendingUp }
                        ].map((item, idx) => (
                            <div key={idx} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">
                                <div className="flex items-start gap-4">
                                    <div className="bg-blue-500/20 p-3 rounded-xl">
                                        <item.icon className="w-5 h-5 text-blue-200" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-1">{item.title}</h4>
                                        <p className="text-sm text-blue-100">{item.desc}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// 6. Complete Infrastructure Showcase - After Facilities
export function InfrastructureShowcase() {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                    Advanced Computing Infrastructure
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Federated cloud services combining AOSP-EA and GOSC servers, dedicated to
                    research, training, and innovation
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
                    <Database className="w-12 h-12 text-blue-600 mb-4" />
                    <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-5xl font-bold text-gray-900">15</span>
                        <span className="text-2xl font-semibold text-blue-600">PB</span>
                    </div>
                    <p className="text-gray-700 font-medium mb-2">Storage Capacity</p>
                    <p className="text-sm text-gray-600">
                        Petabyte-scale storage for large datasets, models, and research outputs
                    </p>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
                    <Cpu className="w-12 h-12 text-indigo-600 mb-4" />
                    <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-5xl font-bold text-gray-900">1,392</span>
                    </div>
                    <p className="text-gray-700 font-medium mb-2">CPU Cores</p>
                    <p className="text-sm text-gray-600">
                        High-performance computing power for AI training and research workloads
                    </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
                    <Server className="w-12 h-12 text-purple-600 mb-4" />
                    <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-5xl font-bold text-gray-900">2</span>
                    </div>
                    <p className="text-gray-700 font-medium mb-2">Federated Servers</p>
                    <p className="text-sm text-gray-600">
                        AOSP-EA and GOSC infrastructure for distributed computing
                    </p>
                </div>
            </div>

            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 text-white">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                            <Cloud className="w-8 h-8" />
                            Federated Cloud Services
                        </h3>
                        <p className="text-gray-300 leading-relaxed mb-6">
                            Our infrastructure enables African researchers, institutions, and innovators to access
                            world-class computing resources without the traditional barriers of cost and geography.
                        </p>
                        <div className="space-y-3">
                            {[
                                'Open access for public & private institutions',
                                'Support for multilingual AI models',
                                'FAIR data principles compliance',
                                'Low-resource optimization'
                            ].map((feature, idx) => (
                                <div key={idx} className="flex items-center gap-3">
                                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    <span className="text-gray-200">{feature}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <Shield className="w-5 h-5 text-blue-400" />
                                <h4 className="font-bold">Secure & Compliant</h4>
                            </div>
                            <p className="text-sm text-gray-300">
                                Enterprise-grade security with data sovereignty considerations
                            </p>
                        </div>

                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <Zap className="w-5 h-5 text-yellow-400" />
                                <h4 className="font-bold">High Performance</h4>
                            </div>
                            <p className="text-sm text-gray-300">
                                Optimized for AI/ML workloads and intensive computations
                            </p>
                        </div>

                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <Users className="w-5 h-5 text-green-400" />
                                <h4 className="font-bold">Democratized Access</h4>
                            </div>
                            <p className="text-sm text-gray-300">
                                Available to researchers, startups, SMEs, and institutions
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Demo component showing all sections together
export default function AICoEEnhanced() {
    return (
        <div className="min-h-screen bg-white">
            {/* Hero would be here with InfrastructureStats */}

            <IntroductionSection />

            {/* Mission & Vision would be here */}

            <StrategicChallenges />

            {/* Objectives would be here */}

            <ThematicPriorities />

            {/* Governance would be here */}

            {/* Functions would be here */}

            {/* Projects would be here */}

            {/* Facilities would be here */}

            <InfrastructureShowcase />

            <GlobalNetworkHighlight />

            {/* Partnership Network would be here */}
        </div>
    );
}